/**
 * Canonical plan-limit copy so every surface names the plan and frames the
 * block as a plan ceiling, not a product bug (SUPENG-249).
 */

type PlanLimitReason =
  | 'split_test_limit'
  | 'heatmap_limit'
  | 'funnel_limit'
  | 'seat_limit'
  | 'website_limit'

const NOUN_TO_REASON: Record<string, PlanLimitReason> = {
  heatmaps: 'heatmap_limit',
  funnels: 'funnel_limit',
  experiments: 'split_test_limit',
  'A/B tests': 'split_test_limit',
  'team members': 'seat_limit',
  seats: 'seat_limit',
  sites: 'website_limit',
}

const REASON_TO_NOUN: Record<PlanLimitReason, string> = {
  split_test_limit: 'A/B tests',
  heatmap_limit: 'heatmaps',
  funnel_limit: 'funnels',
  seat_limit: 'team seats',
  website_limit: 'sites',
}

export function inferPlanLimitReason(noun?: string): PlanLimitReason | undefined {
  if (!noun) return undefined
  return NOUN_TO_REASON[noun]
}

/** "funnels" → "funnel", "A/B tests" → "A/B test", "team members" → "team member". */
export function singularNoun(noun: string): string {
  return noun.endsWith('s') ? noun.slice(0, -1) : noun
}

function formatIncludedCount(limit: number, noun: string): string {
  if (!Number.isFinite(limit)) return noun
  if (limit !== 1) return `${limit} ${noun}`
  if (noun === 'team members') return '1 team member'
  if (noun.endsWith('s')) return `1 ${noun.slice(0, -1)}`
  return `1 ${noun}`
}

export function buildPlanLimitCopy({
  noun,
  limit,
  planName,
  resolution,
}: {
  noun: string
  limit: number
  planName: string
  resolution: string
}): { title: string; description: string } {
  return {
    title: `You've reached the ${planName} plan's ${singularNoun(noun)} limit`,
    description: `Your plan includes ${formatIncludedCount(limit, noun)}. This is a plan limit, not an error. ${resolution}`,
  }
}

export function buildUpgradeTooltip({
  noun,
  limit,
  planName,
  isReadOnly,
  readOnlyReason,
}: {
  noun: string
  limit?: number
  planName: string
  isReadOnly: boolean
  readOnlyReason?: 'canceled' | 'trial_ended' | null
}): string {
  if (isReadOnly) {
    return readOnlyReason === 'trial_ended'
      ? `Your trial has ended. Reactivate to create new ${noun}`
      : `Your subscription has ended. Upgrade to create new ${noun}`
  }
  if (Number.isFinite(limit)) {
    return `You've used ${formatIncludedCount(limit as number, noun)} on the ${planName} plan`
  }
  return `You've reached the ${planName} plan's ${singularNoun(noun)} limit`
}

/** One-line header for PlanExplorerDialog when opened from a limit CTA. */
export function buildPricingModalLimitHeader(
  reason: string | null | undefined,
  planName: string,
): string | null {
  if (!reason || !(reason in REASON_TO_NOUN)) return null
  const noun = REASON_TO_NOUN[reason as PlanLimitReason]
  return `You've used all ${noun} on the ${planName} plan. Pick a plan with more.`
}
